import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { routes } from "@/configs/router.config/routes";
import { getModelFamilyFromModelName } from "@/models/agent/agentModel";
import { AgentRun } from "@/models/agent/agentRun";
import { AgentMessage } from "@/models/agent/message";
import { AgentService } from "@/services/agent/agent.service";
import { useAgentRunMutations } from "./useAgentRunMutations";
import { useThreadMessageMutations } from "./useThreadMessageMutations";
import { useThreadMutations } from "./useThreadMutations";

export const useSendMessage = () => {
  const { workspaceId, threadId } = useParams();
  const navigate = useNavigate();
  const [isSending, setIsSending] = useState(false);
  const { pushOptimisticThread } = useThreadMutations();
  const { pushOptimisticMessage } = useThreadMessageMutations(threadId);

  const { optimisticUpdate, replaceOptimisticRun } = useAgentRunMutations(threadId ?? null);

  // 新規チャット（threadIdなし）でAgent実行
  const handleSendNewMessage =
    (modelName: string, commandName?: string, reasoningEffort?: string) => async (content: string) => {
      if (!workspaceId) return;

      try {
        setIsSending(true);
        const result = await AgentService.executeAgent({
          workspace_id: workspaceId,
          message: content,
          command: commandName,
          model_name: modelName,
          reasoning_effort: reasoningEffort,
        });
        setIsSending(false);

        if (result.thread_id) {
          const metadata = {
            model_name: modelName,
            model_family: getModelFamilyFromModelName(modelName),
            reasoning_effort: reasoningEffort ?? "",
          };
          pushOptimisticThread(result.thread_id, content, metadata);
          navigate(routes.agentThread(workspaceId)(result.thread_id));
        }
      } catch (_error) {
        console.error("Failed to send message:", _error);
        toast.error("メッセージの送信に失敗しました。");
        setIsSending(false);
      }
    };

  const handleSendThreadMessage =
    (threadId: string, modelName: string, commandName?: string, reasoningEffort?: string) =>
    async (
      content: string,
    ) => {
      if (!workspaceId) return;

      // 楽観的ユーザーメッセージ追加
      const optimisticMessage = AgentMessage.createOptimisticMessage(threadId, content);
      pushOptimisticMessage(optimisticMessage);

      // 送信時点で楽観的にAgentRunを追加（APIレスポンス前に "thinking..." を表示するため）
      const optimisticRunId = `optimistic-${Date.now()}`;
      const optimisticRun = new AgentRun(optimisticRunId, threadId, "running", new Date(), null);
      optimisticUpdate(optimisticRun);

      try {
        setIsSending(true);
        const result = await AgentService.executeAgent({
          workspace_id: workspaceId,
          message: content,
          thread_id: threadId,
          command: commandName,
          model_name: modelName,
          reasoning_effort: reasoningEffort,
        });

        // レスポンス後に正しいIDで上書き（optimisticなrunを削除して実runに置き換え）
        const agentRun = new AgentRun(
          result.agent_run_id,
          result.thread_id,
          "running",
          new Date(),
          null,
        );
        replaceOptimisticRun(optimisticRunId, agentRun);

        setIsSending(false);
      } catch (error) {
        console.error("Failed to send message:", error);
        toast.error("メッセージの送信に失敗しました。");

        // 楽観的に追加したAgentRunをfailedに更新してエラー表示を出す
        const failedRun = new AgentRun(optimisticRunId, threadId, "failed", new Date(), new Date());
        optimisticUpdate(failedRun);

        setIsSending(false);
      }
    };

  const handleSendMessage = async (
    content: string,
    modelName: string,
    commandName?: string,
    reasoningEffort?: string,
  ) => {
    if (!workspaceId) return;

    const send = threadId
      ? handleSendThreadMessage(threadId, modelName, commandName, reasoningEffort)
      : handleSendNewMessage(modelName, commandName, reasoningEffort);
    await send(content);
  };

  return {
    handleSendMessage,
    isSending,
  };
};
